import { captureException } from '@sentry/core';

import { Fn } from '@/types/common';
import {
  ArticleFeedbackBase,
  ArticleSendFeedbackReq,
  ArticleSendFeedbackWithCommentReq,
  FeedbackFormValues,
  IsLiked,
  SearchBaseRequest,
  SearchFeedbackLikeReq,
  SearchFeedbackWithCommentReq,
} from '@/types/feedback';

import { backendApiInstance } from './http';

type FeedbackHandlerConfig<_ extends FeedbackFormValues | IsLiked> = {
  endpoint: string;
  baseBody: ArticleFeedbackBase | SearchBaseRequest;
  onComplete?: Fn<_, void>;
  onError?: Fn<unknown, void>;
  onStartRequest?: Fn<void, void>;
  onSuccessRequest?: Fn<void, void>;
};

export function createFeedbackHandler<T extends IsLiked>(
  config: FeedbackHandlerConfig<T> & { baseBody: ArticleFeedbackBase },
): (payload: T) => Promise<void>;

export function createFeedbackHandler<T extends IsLiked>(
  config: FeedbackHandlerConfig<T> & { baseBody: SearchBaseRequest },
): (payload: T) => Promise<void>;

export function createFeedbackHandler<T extends FeedbackFormValues & IsLiked>(
  config: FeedbackHandlerConfig<T> & { baseBody: ArticleFeedbackBase },
): (payload: T) => Promise<void>;

export function createFeedbackHandler<T extends FeedbackFormValues & IsLiked>(
  config: FeedbackHandlerConfig<T> & { baseBody: SearchBaseRequest },
): (payload: T) => Promise<void>;

export function createFeedbackHandler<T extends FeedbackFormValues | IsLiked>(config: FeedbackHandlerConfig<T>) {
  return async (payload: T) => {
    const baseBody = config.baseBody;

    try {
      config.onStartRequest?.();
      type RequestType = typeof baseBody extends ArticleFeedbackBase
        ? T extends FeedbackFormValues & IsLiked
          ? ArticleSendFeedbackWithCommentReq
          : ArticleSendFeedbackReq
        : typeof baseBody extends SearchBaseRequest
          ? T extends FeedbackFormValues & IsLiked
            ? SearchFeedbackWithCommentReq
            : SearchFeedbackLikeReq
          : never;

      const fullBody = { ...baseBody, ...payload } as RequestType;

      await backendApiInstance.post({ url: config.endpoint, body: fullBody });

      config.onSuccessRequest?.(fullBody);
    } catch (error) {
      config.onError?.(error);
      captureException(`Failed to send feedback, ${error}, try send: ${JSON.stringify({ ...baseBody, ...payload })}`, {
        level: 'error',
      });
    } finally {
      config.onComplete?.(payload);
    }
  };
}
